import { getTenantSlugFromHost } from "./hostResolver";
import { safeGetItem } from "@mediatechgroup/app-version";

// Invalid tenant ID patterns - these should be treated as null
export const INVALID_TENANT_IDS = ['null', 'undefined', 'false', 'true', ''];

// Apex public routes — these are tenant-less platform pages served at
// the root of clubs.clubcentral.app (no /:clubid prefix). Treating the
// first path segment as a tenant slug here causes downstream backend
// calls (analytics, settings, tenant lookup) to fail with
// "Failed to resolve tenant database" / PGRST116. Must stay in sync
// with the `apexPublicRoutes` array in App.tsx and the apex
// `<Route path="/..." />` declarations at the bottom of that file.
export const APEX_PUBLIC_PATHS = [
  'sms-opt-in',
  'privacy',
  'privacy-policy',
  'terms',
  'terms-of-service',
];

/**
 * Strict validation for a tenant slug. The single source of truth — callers
 * that need to decide "is this URL segment a real tenant?" (TenantContext,
 * supabaseClient) must use this instead of re-implementing the checks.
 * Rejects the invalid literals, apex public paths, too-short, and non-slug
 * formats.
 */
export const isValidTenantSlug = (slug) => {
  if (!slug) return false;
  const lower = slug.toLowerCase();
  if (INVALID_TENANT_IDS.includes(lower)) return false;
  if (APEX_PUBLIC_PATHS.includes(lower)) return false;
  if (slug.length < 2) return false;
  if (!/^[a-zA-Z0-9-]+$/.test(slug)) return false;
  return true;
};

/**
 * Extract the tenant slug from the first URL path segment, validated.
 * Returns null when the segment isn't a real tenant slug. No localStorage
 * fallback — use this when you specifically want "what tenant does the URL
 * say" (e.g. deciding which tenant to bootstrap). For request headers use
 * getTenantId(), which adds the localStorage fallback.
 */
export const getTenantSlugFromPath = () => {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const slug = pathParts[0] || null;
  return isValidTenantSlug(slug) ? slug : null;
};

/**
 * Get tenant ID from URL path with validation and localStorage fallback.
 * This function is called at request time to ensure it always gets the current URL.
 * Falls back to localStorage if the URL doesn't contain a valid tenant slug.
 */
export const getTenantId = () => {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  let tenantId = pathParts[0] || null;

  // Reject invalid values like literal "null" or "undefined" in the URL
  if (tenantId && INVALID_TENANT_IDS.includes(tenantId.toLowerCase())) {
    tenantId = null;
  }

  // Apex public routes are not tenant slugs. Return null so downstream
  // backend calls don't try to resolve "sms-opt-in" / "privacy" / etc.
  // as a tenant database. Bypass localStorage fallback too — the user
  // is on a tenant-less public page, so any stored tenant slug from a
  // prior session is irrelevant context.
  if (tenantId && APEX_PUBLIC_PATHS.includes(tenantId.toLowerCase())) {
    return null;
  }

  // Host-scoped fallback: on a tenant subdomain (`<slug>.clubcentral.app`)
  // the slug comes from the HOST, not the path. The boot-time path rewrite
  // (index.tsx) normally injects it into the path so this isn't hit, but
  // the host is more authoritative than a possibly-stale localStorage
  // value, so check it before the localStorage fallback.
  if (!tenantId) {
    const hostSlug = getTenantSlugFromHost();
    if (hostSlug) return hostSlug;
  }

  // Fallback to localStorage if URL doesn't have a valid tenant
  if (!tenantId) {
    try {
      tenantId = safeGetItem("tenantSlug");
    } catch {
      // localStorage may be blocked in third-party iframe contexts
    }
  }

  return tenantId;
};

/**
 * Phase 1 subdomains: when on a tenant front-door host
 * (`<slug>.clubcentral.app`) whose path does NOT already carry the tenant
 * slug, return the current path with the slug injected as the first
 * segment (so the existing `/:clubid/...` routes + `/${tenantId}/...`
 * links work unchanged). Returns null when no rewrite is needed:
 *   - platform host (clubs/staging/localhost) → host gives no slug
 *   - path already starts with a valid tenant slug
 *   - tenant-less apex public page (privacy/terms/sms-opt-in)
 * Applied once at boot via history.replaceState (see index.tsx) — pure
 * apart from reading window.location, so it's unit-testable.
 */
export const getHostScopedRewritePath = () => {
  const hostSlug = getTenantSlugFromHost();
  if (!hostSlug) return null; // platform host — leave URL alone

  let location;
  try {
    location = {
      pathname: window.location.pathname || "/",
      search: window.location.search || "",
      hash: window.location.hash || "",
    };
  } catch {
    return null;
  }
  return rewritePathForHostSlug(hostSlug, location);
};

/**
 * The rewrite rule itself, pure: `location` is { pathname, search, hash }.
 * Shared by the phase 1 subdomain rewrite above and the phase 2 custom-domain
 * boot (api/customDomainBoot.ts), so a club's own domain gets exactly the
 * subdomain's rule: only a first segment equal to the club's slug is the
 * slug; every other segment is an app route.
 */
export const rewritePathForHostSlug = (hostSlug, location) => {
  if (!hostSlug) return null;
  const pathname = (location && location.pathname) || "/";
  const search = (location && location.search) || "";
  const hash = (location && location.hash) || "";

  const first = (pathname.split("/").filter(Boolean)[0] || "").toLowerCase();

  // Idempotent: the slug is already the first segment. NOTE we can't use
  // getTenantSlugFromPath() here — it treats any valid-format segment
  // (e.g. "manage", "register") as a slug, so on a subdomain a slug-less
  // deep link like /manage would be wrongly seen as already-prefixed.
  if (first === hostSlug) return null;

  // Don't rewrite tenant-less platform pages under a tenant slug.
  if (APEX_PUBLIC_PATHS.includes(first)) return null;

  const rest = pathname === "/" ? "" : pathname;
  return `/${hostSlug}${rest}${search}${hash}`;
};

/**
 * Get authentication source (central or tenant)
 */
export const getAuthSource = () => {
  try {
    return safeGetItem("authSource");
  } catch {
    return null;
  }
};

/**
 * Get authentication token
 */
export const getAuthToken = () => {
  try {
    return safeGetItem("authToken");
  } catch {
    return null;
  }
};

/**
 * Get common headers for API requests
 */
export const getHeaders = (includeAuth = false) => {
  const headers = {
    "Content-Type": "application/json",
  };

  const tenantId = getTenantId();
  if (tenantId) {
    headers["x-tenant-id"] = tenantId;
  }

  const authSource = getAuthSource();
  if (authSource) {
    headers["x-auth-source"] = authSource;
  }

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers.authorization = `Bearer ${token}`;
    }
  }

  return headers;
};
